<template>
  <div
    class="mx-auto flex flex-col gap-12 max-w-7xl px-4 py-16 sm:px-6 lg:px-8"
  >
    <div class="flex flex-col gap-y-8 mx-auto w-full">
      <div
        class="flex flex-col w-full mx-auto justify-center items-center max-w-[800px] text-center"
      >
        <h1
          class="text-pretty text-3xl font-medium tracking-tight text-gray-900 sm:text-4xl lg:text-balance"
        >
          Frequently asked questions
        </h1>
        <p
          class="mt-4 text-lg sm:text-xl sm:leading-[30px] text-gray-700 text-pretty sm:mt-2"
        >
          Here are some common questions about our platform
        </p>
      </div>

      <div class="flex flex-col items-center justify-center w-full">
        <Tabs
          default-value="tools"
          class="flex flex-col items-center justify-center gap-4 w-full lg:flex-row"
        >
          <div
            v-for="(tab, index) in tabs"
            :key="index"
            :value="tab.value"
            class="w-full lg:w-auto"
          >
            <Button
              size="default"
              :variant="tab.isActive ? 'default' : 'secondary'"
              class="w-full text-sm/6 font-semibold text-center px-4 lg:w-64"
              @click="changeActiveState(tab)"
            >
              <component
                :is="tab.icon"
                :stroke-color="
                  tab.isActive ? '#ffffff' : '#667085'
                "
                class="!size-5 stroke-2"
              />
              {{ tab.label }}
            </Button>

            <!-- Mobile Accordions -->
            <div
              v-if="isMobile && tab.isActive"
              class="w-full"
            >
              <Accordion
                type="single"
                class="w-full mt-4"
                collapsible
              >
                <AccordionItem
                  v-for="item in tab.accordionItems"
                  :key="item.value"
                  :value="item.value"
                >
                  <AccordionTrigger>
                    <p class="text-start">
                      {{ item.title }}
                    </p>
                  </AccordionTrigger>
                  <AccordionContent>
                    {{ item.content }}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </Tabs>
      </div>

      <!-- Desktop Accordions -->
      <template v-if="!isMobile">
        <template
          v-for="(tab, index) in tabs"
          :key="index"
        >
          <Accordion
            v-if="tab.isActive"
            type="single"
            class="w-full"
            collapsible
          >
            <AccordionItem
              v-for="item in tab.accordionItems"
              :key="item.value"
              :value="item.value"
            >
              <AccordionTrigger>
                <p class="text-start">
                  {{ item.title }}
                </p>
              </AccordionTrigger>
              <AccordionContent>
                {{ item.content }}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { shallowRef } from "vue";
import { Tabs } from "@/Components/ui/tabs";
import Button from "@/Components/ui/button/Button.vue";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/Components/ui/accordion";
import PaletteIcon from "@/Components/Icons/PaletteIcon.vue";
import DataflowIcon from "@/Components/Icons/DataflowIcon.vue";
import { Cog8ToothIcon } from "@heroicons/vue/24/outline";
import { useBreakpoints } from "@vueuse/core";

interface AccordionItemType {
    value: string;
    title: string;
    content: string;
}

interface Tab {
    label: string;
    value: string;
    icon: unknown;
    isActive: boolean;
    accordionItems: AccordionItemType[];
}

const designAccordionItems = [
    {
        value: "design-1",
        title: "How do I select a postcard template to start designing?",
        content: "You can select a template from the template gallery.",
    },
    {
        value: "design-2",
        title: "Can I import my own photos or artwork into the postcard design?",
        content: "Yes! You can upload your own photos or artwork.",
    },
    {
        value: "design-3",
        title: "Is there a way to preview how my postcard will look before printing?",
        content: "Yes! You can preview your postcard before printing.",
    },
];

const integrationsAccordionItems = [
    {
        value: "integrations-1",
        title: "How do I integrate my CRM with the platform?",
        content: "You can integrate your CRM with the platform.",
    },
    {
        value: "integrations-2",
        title: "Can I import my contacts from a CSV file?",
        content: "Yes! You can import your contacts from a CSV file.",
    },
    {
        value: "integrations-3",
        title: "Is there a way to automate the sending of postcards?",
        content: "Yes! You can automate the sending of postcards.",
    },
];

const setupAccordionItems = [
    {
        value: "setup-1",
        title: "How do I set up my account?",
        content: "You can set up your account by following the steps.",
    },
    {
        value: "setup-2",
        title: "Can I customize the settings for my account?",
        content: "Yes! You can customize the settings for your account.",
    },
    {
        value: "setup-3",
        title: "Is there a way to track the performance of my postcards?",
        content: "Yes! You can track the performance of your postcards.",
    },
];

const tabs = shallowRef([
    {
        label: "Design",
        value: "design",
        icon: PaletteIcon,
        isActive: true,
        accordionItems: designAccordionItems,
    },
    {
        label: "Integrations",
        value: "integrations",
        icon: DataflowIcon,
        isActive: false,
        accordionItems: integrationsAccordionItems,
    },
    {
        label: "Setup",
        value: "setup",
        icon: Cog8ToothIcon,
        isActive: false,
        accordionItems: setupAccordionItems,
    },
]);

const breakpoints = useBreakpoints({
    mobile: 0,
    tablet: 640,
    laptop: 1024,
    desktop: 1280,
});

const isMobile = breakpoints.between("mobile", "laptop");

function changeActiveState(tab: Tab) {
    const index = tabs.value.findIndex((t) => t.value === tab.value);
    tabs.value[index].isActive = !tabs.value[index].isActive;
}
</script>
